import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { BrandMark, BrandWordmark } from "./LandingPage";
import { api, analyze, fetchSampleData } from "../lib/api";

const SAMPLE_FLAG_KEY = "ll_sample_mode";
const SAMPLE_VARIANT_KEY = "ll_sample_variant";
const REPORT_STORAGE_KEY = "ll_last_report";

/**
 * /sample-report — non-converting visitors land here from the landing page
 * and the ProUnlockDialog. We auto-run an analysis on the same sample data
 * the analyzer uses, save it under sessionStorage so /report renders it,
 * AND flip the SAMPLE_FLAG so the report force-unlocks (AI memo + PDF) and
 * shows a sticky "this is a sample — get yours" CTA.
 *
 * Variants (via `?variant=` query string):
 *   - `healthy`  (default) — Sunrise Bakery, profitable + low-stress
 *   - `stressed` — Roadside Diner, DSCR<1.0, triggers distress flags so
 *     the free "Always free" markers render alongside paid ones
 *
 * No backend entitlement is created. The unlock is local-only and scoped
 * to the session. We pass `sample_mode=true` to /analyze so the run does
 * NOT persist into the reports collection — sample previews never inflate
 * admin counters.
 */
export default function SampleReportPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const variant = (searchParams.get("variant") || "healthy").toLowerCase();
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sample = await fetchSampleData(variant);
        // Run analysis through the real endpoint so the report we show is
        // identical to what a paying user would get — same metrics engine,
        // same benchmarks, same memo. `sample_mode=true` ensures the run
        // is NOT persisted to the reports collection.
        const result = await analyze(sample, true, "owner", true);
        if (cancelled) return;
        sessionStorage.setItem(REPORT_STORAGE_KEY, JSON.stringify(result));
        sessionStorage.setItem(SAMPLE_FLAG_KEY, "1");
        sessionStorage.setItem(SAMPLE_VARIANT_KEY, variant);
        navigate(`/report?sample=1&variant=${encodeURIComponent(variant)}`, { replace: true });
      } catch (err) {
        if (cancelled) return;
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          "Could not load the sample report. Please try again.";
        setError(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  return (
    <div
      className="min-h-screen bg-[#F7F5F0] bg-grain flex items-center justify-center px-6"
      data-testid="sample-report-launcher"
    >
      <div className="text-center max-w-md">
        <div className="flex items-center justify-center mb-7">
          <BrandWordmark size="md" />
        </div>

        {error ? (
          <>
            <p className="font-serif-display text-2xl text-[#A23E2A] tracking-tight">
              Couldn't load the sample.
            </p>
            <p className="text-sm text-[#5C665D] leading-relaxed mt-3">{error}</p>
            <button
              onClick={() => navigate("/analyze")}
              className="mt-7 inline-flex items-center gap-2 text-[#1F382E] hover:text-[#C26D5C] text-sm font-medium transition-colors"
              data-testid="sample-report-error-cta"
            >
              Try the analyzer with your own numbers
              <ArrowRight size={14} strokeWidth={1.5} />
            </button>
          </>
        ) : (
          <>
            <Loader2
              size={28}
              strokeWidth={1.5}
              className="text-[#C26D5C] mx-auto animate-spin"
            />
            <p className="font-serif-display text-2xl text-[#1F382E] tracking-tight mt-6">
              Building your sample report…
            </p>
            <p className="text-sm text-[#5C665D] leading-relaxed mt-3 max-w-sm mx-auto">
              We're running a real analysis on a sample SMB so you can see
              exactly what every paying CFO Grade customer receives —
              including the unlocked AI memo and PDF export.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// Tiny helper so ReportPage doesn't have to duplicate the key name.
export const SAMPLE_MODE_STORAGE_KEY = SAMPLE_FLAG_KEY;

// Convenience: clear the sample flag (used when starting a real analysis).
export function clearSampleMode() {
  if (typeof window !== "undefined") {
    window.sessionStorage.removeItem(SAMPLE_FLAG_KEY);
  }
}

// Expose api for tree-shaking parity, no-op otherwise.
export { api };
